/* eslint-disable no-debugger */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  useEffect,
  useState,
  useRef,
  type ReactElement,
  useContext
} from 'react'
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  Link,
  ListItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Text,
  UnorderedList
} from '@chakra-ui/react'
import { connection, RoomCode } from '../../renderer/roomConnector'
import { RoomSettingsContext } from '../+RoomSettingsContext'
import { ErrorModal, ScreenSizeErrorModal } from '../../components/Modals'
import SideNav from '../../components/SideNav'
import '@fontsource/kalam'
import '@fontsource-variable/caveat'
import { activities, universalRoomOptions } from '../../activities'

declare global {
  interface Window {
    featuredSubmissionIndex: number | null
    phase: string
  }
}

// window.featuredSubmissionIndex = null

interface itemData {
  id: string
  name: string
  submissions: any
  archived: boolean
  deleted: boolean
}

const firstNames = [
  'Alice',
  'Bob',
  'Charlie',
  'David',
  'Ella',
  'Frank',
  'Grace',
  'Hannah',
  'Isaac',
  'Jack',
  'Katherine',
  'Liam',
  'Mia',
  'Nathan',
  'Olivia',
  'Peter',
  'Quincy',
  'Rachel',
  'Samuel',
  'Tiffany',
  'Ursula',
  'Victoria',
  'William',
  'Xavier',
  'Yvonne',
  'Zachary'
]

const colors = ['#286668', '#3EBC87', '#EACE3A', '#F9AB38', '#ED574F']

const tempEntries = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22,
  23, 24, 25
]
  .slice(0, 2)
  .map((value: number) => {
    return {
      id: 'abc1' + value.toString(),
      name: firstNames[Math.floor(Math.random() * firstNames.length)],
      submissions: [
        {
          text: 'Likes math',
          truth: true,
          votes: ['Seve', 'Jess']
        },
        {
          text: 'Likes to eat',
          truth: true,
          votes: ['Nick']
        },
        {
          text: 'mQINBF8Hn J8BEACxLyLorem ipsum dolor sit amet, consectetur adipiscing elit. Quisque accumsan rhoncus dui, sed mattis leo rutrum nec. Vestibul',
          truth: false,
          votes: ['Onalie']
        }
      ],
      archived: false,
      deleted: false
    }
  })
const initialRoomSettings = {
  activity: activities.twoTruthsAndALie,
  info: (
    <>
      <Heading size="sm" mb="0">
        Host
      </Heading>
      <UnorderedList>
        {activities.twoTruthsAndALie.hostInfo.map(
          (text: string, index: number) => {
            return <ListItem key={`hostInfo-${index}`}>{text}</ListItem>
          }
        )}
      </UnorderedList>
      <Heading size="sm" mb="0" mt="20px">
        Audience
      </Heading>
      <UnorderedList>
        {activities.twoTruthsAndALie.audienceInfo.map(
          (text: string, index: number) => {
            return <ListItem key={`audienceInfo-${index}`}>{text}</ListItem>
          }
        )}
      </UnorderedList>
    </>
  ),
  options: {
    ...universalRoomOptions
  }
}

const MIN_PLAYERS = 2

let CHANNEL_GLOBAL: any = null

let orbitInteral: any

interface PostItData {
  text: string
  color: string
  style?: any
  onClick?: any
}

const PostIt: React.FC<PostItData> = (props) => {
  const maxAngle = 5
  const [rotations] = useState(0 - maxAngle + Math.random() * 2 * maxAngle)
  const { color, text, style, onClick } = props
  const bgColors = {
    start: 'green.300',
    stop: 'red.300',
    continue: 'yellow.300'
  }[color]

  return (
    <Box as="button" w="100%" onClick={onClick}>
      <Box
        sx={{
          bg: bgColors,
          boxShadow: '2px 2px 5px rgba(0,0,0,0.5)',
          aspectRatio: '1',
          width: '90%',
          mx: 'auto',
          mb: '10px',
          p: '20px',
          transition: 'all 500ms ease',
          transform: `rotate(${rotations}deg)`,
          pos: 'relative',
          fontFamily: 'Kalam, cursive'
        }}
        style={style}
      >
        <Box
          sx={{
            overflow: 'hidden',
            maxH: '10em',
            display: '-webkit-box',
            lineHeight: '1.25em',
            textAlign: 'left'
          }}
          style={{
            fontSize: `calc(1.5vw + (40vw / ${
              text.length < 4 ? 4 : text.length
            }))`
          }}
        >
          {text}
        </Box>
      </Box>
    </Box>
  )
}

const Page: React.FC = () => {
  const [roomCode, setRoomCode] = useState<string>('')
  const { roomSettings, setRoomSettings } = useContext(RoomSettingsContext)
  const [preventRefresh, setPreventRefresh] = useState<boolean>(false)
  const [triggerRefresh, setTriggerRefresh] = useState<boolean>(false)
  const [isConnecting, setIsConnecting] = useState<boolean>(false)

  const [featuredSubmissionIndex, setFeaturedSubmissionIndex] = useState<
    number | null
  >(null)
  const [submissions, setSubmissions] = useState<itemData[]>(
    import.meta.env.MODE === 'development' ? tempEntries : []
  )
  const [globalRotation, setGlobalRotation] = useState<number>(0)
  const [phase, setPhase] = useState<string>('setup')

  const [csvData, setCsvData] = useState<string[][]>([])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const refreshList = (): void => {
    CHANNEL_GLOBAL.send({
      action: 'updateSubmissionList',
      submissions
    })
  }

  const startVote = (): void => {
    CHANNEL_GLOBAL.send({ action: 'nextVote' })
  }

  const updateItems = (
    message: {
      id: string
      clientId: string
      desciption: string
      type: string
      submission?: any[]
      vote?: number
    },
    channel: any
  ): void => {
    if (message.type === 'submission') {
      setSubmissions((prevValue) => {
        const tempSubmissions = [
          ...prevValue,
          {
            id: message.id + new Date().toISOString(),
            name: message.clientId,
            submissions: message.submission!.sort(
              (a: any, b: any) => Math.random() - 0.5
            ),
            archived: false,
            deleted: false
          }
        ]
        return tempSubmissions
      })
    }
    if (
      message.type === 'vote' &&
      ['submission', 'fadeInSubmission'].includes(window.phase)
    ) {
      setSubmissions((prevVal: any) => {
        const newPreVal: any = [...prevVal]
        if (window.featuredSubmissionIndex !== null) {
          newPreVal[window.featuredSubmissionIndex].submissions.forEach(
            (item: any, index: number) => {
              const newVotes = item.votes.filter(
                (val: string) => val !== message.clientId
              )
              if (index === message.vote) {
                newVotes.push(message.clientId)
              }
              if (window.featuredSubmissionIndex !== null) {
                newPreVal[window.featuredSubmissionIndex].submissions[
                  index
                ].votes = newVotes
              }
            }
          )
        }
        return newPreVal
      })
    }
  }

  const triggerAction = (message: any, channel: any): void => {
    if (message.action !== 'updateSubmissionList') {
      updateItems(message, channel)
    }
  }

  const triggerEnter = (message: any, channel: any): void => {
    setTriggerRefresh(true)
  }

  const TTAALCallback = (results: any): void => {
    if (results !== false) {
      setRoomCode(results.roomCode)
      CHANNEL_GLOBAL = results.channel
    } else {
      setErrorMessage('Failed to connect, try refreshing the window')
    }
  }

  useEffect(() => {
    setRoomSettings(initialRoomSettings)
    if (!isConnecting) {
      setIsConnecting(true)
      connection('ttaal', TTAALCallback, { triggerAction, triggerEnter })
    }
  }, [])

  useEffect(() => {
    const newCSVData = [
      [
        'id',
        'Name',
        'Fact1',
        'Fact1 Votes',
        'Fact1 Truth',
        'Fact2',
        'Fact2 Votes',
        'Fact2 Truth',
        'Fact3',
        'Fact3 Votes',
        'Fact3 Truth',
        'Answered',
        'Deleted'
      ]
    ]
    submissions.forEach((item) => {
      newCSVData.push([
        item.id,
        item.name,
        item.submissions[0].text,
        item.submissions[0].votes.join(', '),
        item.submissions[0].truth === true ? 'true' : 'false',
        item.submissions[1].text,
        item.submissions[1].votes.join(', '),
        item.submissions[1].truth === true ? 'true' : 'false',
        item.submissions[2].text,
        item.submissions[2].votes.join(', '),
        item.submissions[2].truth === true ? 'true' : 'false',
        item.archived ? 'true' : 'false',
        item.deleted ? 'true' : 'false'
      ])
    })
    if (CHANNEL_GLOBAL !== null && !preventRefresh) {
      refreshList()
    }
    setPreventRefresh(false)
    setCsvData(newCSVData)
  }, [submissions])

  useEffect(() => {
    if (CHANNEL_GLOBAL !== null) {
      refreshList()
      setTriggerRefresh(false)
    }
  }, [triggerRefresh])

  useEffect(() => {
    window.phase = phase
    switch (phase) {
      case 'setup':
        clearInterval(orbitInteral)
        orbitInteral = setInterval(() => {
          setGlobalRotation((prevVal) => prevVal + 0.5)
        }, 200)
        break
      case 'fadeInSubmission':
        setSubmissions((prevValue) => {
          if (window.featuredSubmissionIndex !== null) {
            prevValue[window.featuredSubmissionIndex].archived = true
          }
          return prevValue
        })

        clearInterval(orbitInteral)

        transitionTo('submission', 500)
        break
      case 'revealSubmission':
        break
      case 'fadeOutSetup':
        transitionTo('fadeOutSubmission', 500)
        break
      case 'fadeOutSubmission':
        pickRandomSubmission()
        if (window.featuredSubmissionIndex !== null) {
          transitionTo('fadeInSubmission', 500)
        } else {
          transitionTo('fadeInEnd', 500)
        }
        break
      case 'fadeInEnd':
        transitionTo('end', 500)
        break
      default:
    }
  }, [phase])

  const pickRandomSubmission = (): void => {
    const filteredIndexes = submissions
      .map((item, index) => {
        if (item.archived) {
          return -1
        }
        if (item.deleted) {
          return -1
        }
        return index
      })
      .filter((item) => item >= 0)

    if (filteredIndexes.length > 0) {
      const newIndex = (filteredIndexes.length * Math.random()) | 0
      setFeaturedSubmissionIndex(filteredIndexes[newIndex])
      window.featuredSubmissionIndex = filteredIndexes[newIndex]
      startVote()
    } else {
      setFeaturedSubmissionIndex(null)
      window.featuredSubmissionIndex = null
    }
  }

  const startGame = (): void => {
    setPhase('fadeOutSetup')
  }

  const transitionTo = (phase: string, time: number): void => {
    setTimeout(() => {
      setPhase(phase)
    }, time)
  }

  const nameBubbles: any[] = [...submissions].slice(0, 16)

  if (nameBubbles.length > 12 && nameBubbles.length % 2 === 1) {
    nameBubbles.push(null)
  }

  const bubbleSize =
    nameBubbles.length > 5
      ? nameBubbles.length > 12
        ? 10 - nameBubbles.length * 0.2
        : 10 - nameBubbles.length * 0.2
      : 10

  return (
    <Box sx={styles.container}>
      {/* Startup Screen */}
      <Flex
        pos="absolute"
        left="0"
        top="0"
        right="0"
        bottom="0"
        justifyContent="center"
        alignItems="center"
        transition="all 500ms ease"
        opacity={phase === 'setup' ? 1 : 0}
        display={['setup', 'fadeOutSetup'].includes(phase) ? 'flex' : 'none'}
      >
        {nameBubbles.map(
          (item: itemData | null, index: number): ReactElement => {
            const rotation = (360 / nameBubbles.length) * index + globalRotation
            const transformOrigin =
              nameBubbles.length > 12 ? (index % 2 === 0 ? 41 : 30) : 41
            const top =
              nameBubbles.length > 12 ? (index % 2 === 0 ? 0 : 5.5) : 0
            const fontSize = nameBubbles.length > 12 ? 2.8 : 3.5

            return (
              <Box
                w={`${bubbleSize}vw`}
                h={`${bubbleSize}vw`}
                left={`calc(50% - ${bubbleSize / 2}vw)`}
                top={`calc(${top}vw + 30px)`}
                key={item === null ? `placeholder-${index}` : item.id}
                position="absolute"
                // outline={`1px ${(item === null ? 'dashed' : 'solid')} currentcolor`}
                color={'currentcolor'}
                style={{
                  transform: `rotate(${rotation}deg)`
                }}
                transition="all 200ms linear"
                transformOrigin={`50% calc(${transformOrigin}vh - 0vw)`}
                borderRadius={`${bubbleSize}vw`}
                opacity={item === null || item.archived ? '0.5' : 1}
              >
                {item !== null && (
                  <Flex
                    w={`${bubbleSize}vw`}
                    h={`${bubbleSize}vw`}
                    borderRadius={`${bubbleSize}vw`}
                    style={{
                      transform: `rotate(${0 - rotation}deg)`
                    }}
                    transition="all 200ms linear"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Text
                      fontSize={`${fontSize}vw`}
                      textAlign="center"
                      fontWeight={800}
                      letterSpacing="-0.1em"
                      position="relative"
                      sx={{
                        '&:before': {
                          WebkitTextStrokeWidth: '0.03em',
                          WebkitTextStrokeColor: colors[index % colors.length],
                          content: `"${item.name}"`,
                          position: 'absolute',
                          top: '0',
                          left: '0',
                          zIndex: '-1'
                        },
                        '&:after': {
                          content: `"${item.name}"`,
                          position: 'absolute',
                          top: '0.125em',
                          left: '0',
                          zIndex: '-1',
                          textShadow: 'none',
                          backgroundImage: `
                          linear-gradient(
                            45deg,
                            transparent 45%,
                            ${colors[index % colors.length]}aa 45%,
                            ${colors[index % colors.length]}aa 55%,
                            transparent 0
                            )`,
                          backgroundSize: '.025em .025em',
                          backgroundClip: 'text',
                          textFillColor: 'transparent',

                          animation: 'shad-anim 15s linear infinite'
                        }
                      }}
                    >
                      {item.name}
                    </Text>
                  </Flex>
                )}
              </Box>
            )
          }
        )}
        {submissions.length >= MIN_PLAYERS && (
          <Box
            as="button"
            mb="5.5vh"
            borderRadius="10vw"
            pos="absolute"
            color="#ED574F"
            letterSpacing="-0.005em"
            fontWeight={800}
            fontSize="5vw"
            onClick={startGame}
            transform="rotate(-5deg)"
            sx={{
              '&:before': {
                WebkitTextStrokeWidth: '0.03em',
                WebkitTextStrokeColor: '000000aa',
                content: '"Start"',
                position: 'absolute',
                top: '0',
                left: '0',
                zIndex: '-1'
              },
              '&:after': {
                content: '"Start"',
                position: 'absolute',
                top: '0.125em',
                left: '0',
                zIndex: '-1',
                textShadow: 'none',
                transition: 'top 250ms ease',
                backgroundImage: `
                linear-gradient(
                  45deg,
                  transparent 45%,
                  #000000aa 45%,
                  #000000aa 55%,
                  transparent 0
                  )`,
                backgroundSize: '.05em .05em',
                backgroundClip: 'text',
                textFillColor: 'transparent',

                animation: 'shad-anim 15s linear infinite'
              },
              _hover: {
                '&:after': {
                  top: '0.2em'
                }
              }
            }}
          >
            Start
          </Box>
        )}
        {submissions.length < MIN_PLAYERS && (
          <Box
            mb="5.5vh"
            borderRadius="10vw"
            pos="absolute"
            color="#ED574F"
            letterSpacing="-0.005em"
            lineHeight="1em"
            fontWeight={800}
            fontSize="3.5vw"
            transform="rotate(-5deg)"
            textAlign="center"
            sx={{
              '&:before': {
                WebkitTextStrokeWidth: '0.03em',
                WebkitTextStrokeColor: '000000aa',
                content: `"${MIN_PLAYERS}+ Players\\ARequired"`,
                whiteSpace: 'pre',
                textAlign: 'center',
                lineHeight: '1em',
                position: 'absolute',
                top: '0',
                left: '0',
                zIndex: '-1'
              },
              '&:after': {
                content: `"${MIN_PLAYERS}+ Players\\ARequired"`,
                whiteSpace: 'pre',
                lineHeight: '1em',
                position: 'absolute',
                textAlign: 'center',
                top: '0.125em',
                left: '0',
                zIndex: '-1',
                textShadow: 'none',
                transition: 'top 250ms ease',
                backgroundImage: `
                linear-gradient(
                  45deg,
                  transparent 45%,
                  #000000aa 45%,
                  #000000aa 55%,
                  transparent 0
                  )`,
                backgroundSize: '.05em .05em',
                backgroundClip: 'text',
                textFillColor: 'transparent',

                animation: 'shad-anim 15s linear infinite'
              },
              _hover: {
                '&:after': {
                  top: '0.2em'
                }
              }
            }}
          >
            {MIN_PLAYERS}+ Players
            <br />
            Required
          </Box>
        )}
      </Flex>
      {/* end Startup screen */}

      {/* Guessing screen */}
      <Flex
        pos="absolute"
        left="0"
        top="0"
        right="0"
        bottom="0"
        justifyContent="center"
        alignItems="flex-start"
        transition="all 500ms ease"
        opacity={
          ['submission', 'revealSubmission', 'fadeInSubmission'].includes(phase)
            ? 1
            : 0
        }
        display={
          [
            'fadeOutSubmission',
            'fadeInSubmission',
            'submission',
            'revealSubmission'
          ].includes(phase)
            ? 'flex'
            : 'none'
        }
      >
        {featuredSubmissionIndex !== null && (
          <Box>
            <Text
              fontSize="10vw"
              fontWeight={800}
              letterSpacing="-0.9vw"
              lineHeight="1em"
              position="relative"
              mb="0.25em"
              sx={{
                '&:before': {
                  WebkitTextStrokeWidth: '0.03em',
                  WebkitTextStrokeColor:
                    colors[featuredSubmissionIndex % colors.length],
                  content: `"${submissions[featuredSubmissionIndex].name}"`,
                  position: 'absolute',
                  top: '0',
                  left: '0',
                  zIndex: '-1',
                  opacity: '0.5'
                },
                '&:after': {
                  WebkitTextStrokeWidth: '0px',
                  WebkitTextStrokeColor: 'black',
                  content: `"${submissions[featuredSubmissionIndex].name}"`,
                  position: 'absolute',
                  top: '0.125em',
                  left: '0',
                  zIndex: '-2',
                  textShadow: 'none',
                  backgroundImage: `
                        linear-gradient(
                          45deg,
                          transparent 45%,
                          ${
                            colors[featuredSubmissionIndex % colors.length]
                          } 45%,
                          ${
                            colors[featuredSubmissionIndex % colors.length]
                          } 55%,
                          transparent 0
                          )`,
                  backgroundSize: '.03em .03em',
                  backgroundClip: 'text',
                  textFillColor: 'transparent',

                  animation: 'shad-anim 50s linear infinite'
                }
              }}
            >
              {submissions[featuredSubmissionIndex].name}
            </Text>
            <Grid templateColumns="1fr 1fr 1fr" w="90vw">
              {submissions[featuredSubmissionIndex].submissions.map(
                (submission: any, index: number) => {
                  return (
                    <GridItem
                      key={
                        submissions[featuredSubmissionIndex].id +
                        '-' +
                        index.toString()
                      }
                      px="2vw"
                    >
                      <PostIt
                        text={submission.text}
                        color={
                          phase !== 'revealSubmission'
                            ? 'continue'
                            : submission.truth === true
                              ? 'start'
                              : 'stop'
                        }
                        onClick={() => {
                          setPhase((prevVal) => {
                            if (prevVal === 'revealSubmission') {
                              return 'fadeOutSubmission'
                            } else {
                              return 'revealSubmission'
                            }
                          })
                        }}
                      />
                      <Text
                        fontSize="5vw"
                        w="100%"
                        textAlign="center"
                        fontWeight={700}
                        fontFamily="handwritten"
                        letterSpacing="-0.9vw"
                        lineHeight="1em"
                        mb="0.25em"
                      >
                        <Box
                          display="inline-block"
                          as="span"
                          style={{
                            textIndent: '-0.5em',
                            letterSpacing: '-0.125em',
                            fontWeight: '500',
                            animation: `wiggleText 500ms ease ${
                              index * 200
                            }ms infinite normal forwards`
                          }}
                        >
                          {submission.votes.length}
                        </Box>
                      </Text>
                    </GridItem>
                  )
                }
              )}
            </Grid>
          </Box>
        )}
        <Box
          as="button"
          mb="0.5vh"
          borderRadius="10vw"
          pos="absolute"
          color="#ED574F"
          letterSpacing="-0.005em"
          fontWeight={800}
          fontSize="min(64px, 5vh)"
          onClick={() => {
            setPhase((prevVal) => {
              if (prevVal === 'revealSubmission') {
                return 'fadeOutSubmission'
              } else {
                return 'revealSubmission'
              }
            })
          }}
          transform="rotate(-5deg)"
          left="20px"
          bottom="2vh"
          sx={{
            '&:before': {
              WebkitTextStrokeWidth: '0.03em',
              WebkitTextStrokeColor: '000000aa',
              content: ['revealSubmission', 'fadeOutSubmission'].includes(phase)
                ? '"Next"'
                : '"Reveal!"',
              position: 'absolute',
              top: '0',
              left: '0',
              zIndex: '-1'
            },
            '&:after': {
              content: ['revealSubmission', 'fadeOutSubmission'].includes(phase)
                ? '"Next"'
                : '"Reveal!"',
              position: 'absolute',
              top: '0.125em',
              left: '0',
              zIndex: '-1',
              textShadow: 'none',
              transition: 'top 250ms ease',
              backgroundImage: `
                    linear-gradient(
                      45deg,
                      transparent 45%,
                      #000000aa 45%,
                      #000000aa 55%,
                      transparent 0
                      )`,
              backgroundSize: '.05em .05em',
              backgroundClip: 'text',
              textFillColor: 'transparent',

              animation: 'shad-anim 15s linear infinite'
            },
            _hover: {
              '&:after': {
                top: '0.2em'
              }
            }
          }}
        >
          {['revealSubmission', 'fadeOutSubmission'].includes(phase)
            ? 'Next'
            : 'Reveal!'}
        </Box>
      </Flex>
      {/* end Guessing Screen */}

      {/* End Screen */}
      <Flex
        pos="absolute"
        left="0"
        top="0"
        right="0"
        bottom="0"
        justifyContent="center"
        alignItems="center"
        transition="all 500ms ease"
        opacity={phase === 'end' ? 1 : 0}
        display={['end', 'fadeInEnd'].includes(phase) ? 'flex' : 'none'}
      >
        <Box
          as="button"
          mb="5.5vh"
          borderRadius="10vw"
          pos="absolute"
          color="#ED574F"
          letterSpacing="-0.005em"
          fontWeight={800}
          fontSize="5vw"
          onClick={() => {
            window.location.reload()
          }}
          transform="rotate(-5deg)"
          sx={{
            '&:before': {
              WebkitTextStrokeWidth: '0.03em',
              WebkitTextStrokeColor: '000000aa',
              content: '"Play Again?"',
              position: 'absolute',
              top: '0',
              left: '0',
              zIndex: '-1'
            },
            '&:after': {
              content: '"Play Again?"',
              position: 'absolute',
              top: '0.125em',
              left: '0',
              zIndex: '-1',
              textShadow: 'none',
              transition: 'top 250ms ease',
              backgroundImage: `
                    linear-gradient(
                      45deg,
                      transparent 45%,
                      #000000aa 45%,
                      #000000aa 55%,
                      transparent 0
                      )`,
              backgroundSize: '.05em .05em',
              backgroundClip: 'text',
              textFillColor: 'transparent',

              animation: 'shad-anim 15s linear infinite'
            },
            _hover: {
              '&:after': {
                top: '0.2em'
              }
            }
          }}
        >
          Play Again?
        </Box>
      </Flex>
      {/* End Screen End  */}
      <ErrorModal
        isOpen={errorMessage !== null}
        onClose={() => {
          setErrorMessage(null)
        }}
      >
        {errorMessage}
      </ErrorModal>
      <ScreenSizeErrorModal />

      <RoomCode roomCode={roomCode} />
      <SideNav
        download={{
          data: csvData,
          filename: `two-truths-and-a-lie-${
            new Date().toISOString().split('T')[0]
          }.csv`
        }}
      />
      <style>
        {`
        @keyframes wiggleText {
            0%, 66.001% {
              font-weight: 400;
              transform: rotate(2deg);
            }

            0.001%,33% {
              font-weight: 500;
              transform: rotate(-3deg);
            }

            33.001%,66% {
              font-weight: 400;
              transform: rotate(-0deg);
            }
          }
          @keyframes shad-anim {
            0% {background-position: 0 0}
            0% {background-position: 100% -100%}
            }
          `}
      </style>
    </Box>
  )
}

const styles = {
  container: {
    pos: 'absolute',
    height: '100%',
    width: '100%',
    left: 0,
    right: 0,
    bottom: 0,
    top: 0,
    p: '20px'
  },
  grid: {
    pos: 'absolute',
    height: '100%',
    width: '100%',
    left: 0,
    right: 0,
    bottom: 0,
    top: 0
  }
}

export { Page }
